import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Badge, CardHead, Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { formatDateTime, formatNumber, relativeTime } from '../../lib/format.js';

/**
 * The log watcher (0181): what Claude found in the switch's logs, and the morning digests. Read and
 * report only; urgent findings are also emailed to the platform's admins the moment they are made.
 */

interface Finding {
  id: string; created_at: string; severity: 'info' | 'warning' | 'urgent'; title: string; detail: string;
  units: string[]; emailed_at: string | null; dismissed_at: string | null;
}
interface Digest { day: string; subject: string; body: { summary: string; sections: Array<{ heading: string; points: string[] }> }; created_at: string }
interface LogWatch {
  findings: Finding[]; digests: Digest[];
  spend: Array<{ day: string; calls: number; input_tokens: string; output_tokens: string; cache_read_tokens: string; cost_usd: string }>;
  last_batch_at: string | null; daily_cap_usd: number;
  month: { cost_usd: number; calls: number; days: number };
}

const TONE = { urgent: 'danger', warning: 'warn', info: 'info' } as const;
const usd = (n: number) => `US$${n.toFixed(2)}`;
const tokens = (s: string) => formatNumber(Number(s));

/** Today, the month so far and where the month is heading, then the days. */
function Usage({ d }: { d: LogWatch }) {
  const todayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(new Date());
  const today = d.spend.find((r) => r.day === todayKey);
  const spentToday = Number(today?.cost_usd ?? 0);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const projected = d.month.days > 0 ? (d.month.cost_usd / d.month.days) * daysInMonth : 0;
  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <CardHead title="Claude usage" sub={`Opus 5.5. Reading stops for the day at ${usd(d.daily_cap_usd)}; you are told at 80% and emailed at the limit.`} />
      <div className="arc-stat-group">
        <div className="arc-stat"><div className="arc-stat-body">
          <div className="arc-stat-label">Today</div>
          <div className="arc-stat-value arc-num">{usd(spentToday)}</div>
          <div className="arc-stat-sub">{Math.round((spentToday / d.daily_cap_usd) * 100)}% of the daily limit, {formatNumber(today?.calls ?? 0)} calls</div>
        </div></div>
        <div className="arc-stat"><div className="arc-stat-body">
          <div className="arc-stat-label">This month</div>
          <div className="arc-stat-value arc-num">{usd(d.month.cost_usd)}</div>
          <div className="arc-stat-sub">{formatNumber(d.month.calls)} calls</div>
        </div></div>
        <div className="arc-stat"><div className="arc-stat-body">
          <div className="arc-stat-label">A month at this rate</div>
          <div className="arc-stat-value arc-num">{usd(projected)}</div>
          <div className="arc-stat-sub">from {d.month.days} day{d.month.days === 1 ? '' : 's'} of use</div>
        </div></div>
      </div>
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead><tr><th>Day</th><th className="arc-td-right">Calls</th><th className="arc-td-right">Tokens in</th><th className="arc-td-right">Of which cached</th><th className="arc-td-right">Tokens out</th><th className="arc-td-right">Cost</th></tr></thead>
          <tbody>
            {d.spend.slice(0, 14).map((r) => (
              <tr key={r.day}>
                <td className="arc-num">{r.day}</td>
                <td className="arc-num arc-td-right">{formatNumber(r.calls)}</td>
                <td className="arc-num arc-td-right">{tokens(r.input_tokens)}</td>
                <td className="arc-num arc-td-right">{tokens(r.cache_read_tokens)}</td>
                <td className="arc-num arc-td-right">{tokens(r.output_tokens)}</td>
                <td className="arc-num arc-td-right">{usd(Number(r.cost_usd))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.spend.length === 0 && <Empty icon="check" title="No calls yet" hint="A quiet quarter of an hour costs nothing: Claude is only asked when something in the logs is new or spiking." />}
    </div>
  );
}

export function LogWatchPage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['log-watch'], queryFn: () => api.get<LogWatch>('/v1/platform/log-watch'), refetchInterval: 60_000 });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/v1/platform/log-watch/findings/${id}/dismiss`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['log-watch'] }),
  });
  const [showDismissed, setShowDismissed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const d = data.data;
  const findings = (d?.findings ?? []).filter((f) => showDismissed || !f.dismissed_at);
  const stale = d && (!d.last_batch_at || Date.now() - new Date(d.last_batch_at).getTime() > 30 * 60_000);

  return (
    <>
      <SectionHead title="Log watch"
        sub={d ? `Claude reads the switch's logs every fifteen minutes and reports what matters. Last logs received ${d.last_batch_at ? relativeTime(d.last_batch_at) : 'never'}.` : ''} />
      {data.error && <ErrorBanner error={data.error} />}
      {stale && (
        <div className="arc-banner arc-banner-warn" role="alert" style={{ marginBottom: 16 }}>
          <span>No logs have arrived from the switch in the last half hour. The agent may be down or unable to reach the API.</span>
        </div>
      )}

      <div className="arc-card" style={{ marginBottom: 16 }}>
        <CardHead title="Findings" action={
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
            <input type="checkbox" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} /> Show dismissed
          </label>
        } />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>When</th><th>Severity</th><th>Finding</th><th>Where</th><th /></tr></thead>
            {!d ? <TableSkeleton query={data} cols={5} /> : (
              <tbody>
                {findings.map((f) => (
                  <tr key={f.id} style={f.dismissed_at ? { opacity: .55 } : undefined}>
                    <td className="arc-num" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(f.created_at, 'Africa/Johannesburg')}</td>
                    <td><Badge tone={TONE[f.severity]}>{f.severity}</Badge>{f.emailed_at && <span className="arc-subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>emailed</span>}</td>
                    <td>
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ padding: 0, fontWeight: 550, textAlign: 'left' }}
                        aria-expanded={open === f.id} onClick={() => setOpen(open === f.id ? null : f.id)}>
                        {f.title}
                      </button>
                      {open === f.id && <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontSize: 13 }}>{f.detail}</p>}
                    </td>
                    <td className="arc-mono" style={{ fontSize: 12 }}>{f.units.join(', ')}</td>
                    <td className="arc-td-right">
                      {!f.dismissed_at && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate(f.id)}>
                          Dismiss
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {d && findings.length === 0 && <Empty icon="check" title="Nothing to report" hint="Findings appear here when Claude sees something in the logs worth a look." />}
        {dismiss.error && <div className="arc-card-body"><ErrorBanner error={dismiss.error} /></div>}
      </div>

      <div className="arc-card" style={{ marginBottom: 16 }}>
        <CardHead title="Morning digests" sub="Sent to the platform's admins each day after 06:00." />
        <div className="arc-card-body arc-stack" style={{ gap: 16 }}>
          {d && d.digests.length === 0 && <p className="arc-muted" style={{ margin: 0 }}>The first digest goes out tomorrow morning.</p>}
          {d?.digests.map((g) => (
            <details key={g.day} open={g === d.digests[0]}>
              <summary style={{ cursor: 'pointer', fontWeight: 550 }}>{g.day}: {g.subject}</summary>
              <p style={{ margin: '8px 0' }}>{g.body.summary}</p>
              {g.body.sections.map((s) => (
                <div key={s.heading} style={{ marginBottom: 8 }}>
                  <strong style={{ fontSize: 13 }}>{s.heading}</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13 }}>{s.points.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              ))}
            </details>
          ))}
        </div>
      </div>

      {d && <Usage d={d} />}
    </>
  );
}
